<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-chart-box-outline me-2"></i>{{ $t('page.reports', 'Reports & KPIs') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap align-items-center">
          <div class="hms-daterange">
            <label class="hms-daterange-lbl">From</label>
            <input type="date" class="hms-input hms-filter" v-model="startDate" :max="endDate || today" @change="onDateChange" />
            <label class="hms-daterange-lbl">To</label>
            <input type="date" class="hms-input hms-filter" v-model="endDate" :min="startDate" :max="today" @change="onDateChange" />
          </div>
          <div class="hms-preset-chips">
            <button v-for="p in presets" :key="p.key" type="button"
                    class="hms-chip" :class="{ active: activePreset === p.key }"
                    @click="applyPreset(p.key)">{{ p.label }}</button>
          </div>
          <select class="hms-input hms-filter" v-model="branchFilter" @change="loadAll">
            <option value="">All branches</option>
            <option v-for="b in branches" :key="b.branch_id" :value="b.branch_id">{{ b.branch_name }}</option>
          </select>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
          <button class="hms-btn hms-btn-blue" @click="printReport"><i class="mdi mdi-printer me-1"></i>Print</button>
        </div>
      </div>

      <div v-if="loading && !data.Operations" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else-if="data.Operations" class="hms-print-area">
        <!-- Headline KPIs -->
        <div class="hms-kpi-grid">
          <div class="hms-kpi" :class="'k-blue'">
            <i class="mdi mdi-account-group hms-kpi-icon"></i>
            <div>
              <div class="hms-kpi-num">{{ formatNum(data.Operations.total_visits) }}</div>
              <div class="hms-kpi-lbl">Total Visits</div>
              <div class="hms-kpi-sub">{{ formatNum(data.Operations.opd_visits) }} OPD · {{ formatNum(data.Operations.ed_visits) }} ED</div>
            </div>
          </div>
          <div class="hms-kpi k-purple">
            <i class="mdi mdi-bed hms-kpi-icon"></i>
            <div>
              <div class="hms-kpi-num">{{ formatNum(data.Operations.new_admissions) }}</div>
              <div class="hms-kpi-lbl">Admissions</div>
              <div class="hms-kpi-sub">{{ formatNum(data.Operations.current_admissions) }} active · {{ formatNum(data.Operations.discharges) }} discharged</div>
            </div>
          </div>
          <div class="hms-kpi k-green">
            <i class="mdi mdi-cash-multiple hms-kpi-icon"></i>
            <div>
              <div class="hms-kpi-num">KSh {{ formatNum(data.RevenueTotals.revenue_period) }}</div>
              <div class="hms-kpi-lbl">Revenue</div>
              <div class="hms-kpi-sub">{{ formatNum(data.RevenueTotals.billed_period) }} billed</div>
            </div>
          </div>
          <div class="hms-kpi k-red">
            <i class="mdi mdi-cash-clock hms-kpi-icon"></i>
            <div>
              <div class="hms-kpi-num">KSh {{ formatNum(data.RevenueTotals.outstanding) }}</div>
              <div class="hms-kpi-lbl">Outstanding</div>
              <div class="hms-kpi-sub">{{ formatNum(data.RevenueTotals.insurance_pending_count) }} insurance pending</div>
            </div>
          </div>
          <div class="hms-kpi k-amber">
            <i class="mdi mdi-medical-bag hms-kpi-icon"></i>
            <div>
              <div class="hms-kpi-num">{{ formatNum(data.Operations.surgeries) }}</div>
              <div class="hms-kpi-lbl">Surgeries</div>
              <div class="hms-kpi-sub">Avg LOS {{ data.Operations.avg_los ? Number(data.Operations.avg_los).toFixed(1) : '0' }}d</div>
            </div>
          </div>
          <div class="hms-kpi k-info">
            <i class="mdi mdi-account-heart-outline hms-kpi-icon"></i>
            <div>
              <div class="hms-kpi-num">{{ formatNum(data.Operations.new_patients) }}</div>
              <div class="hms-kpi-lbl">New Patients</div>
              <div class="hms-kpi-sub">{{ formatNum(data.Operations.upcoming_appointments) }} upcoming appts</div>
            </div>
          </div>
          <div class="hms-kpi" :class="data.Operations.deaths > 0 ? 'k-red' : 'k-neutral'">
            <i class="mdi mdi-grave-stone hms-kpi-icon"></i>
            <div>
              <div class="hms-kpi-num">{{ formatNum(data.Operations.deaths) }}</div>
              <div class="hms-kpi-lbl">Deaths</div>
            </div>
          </div>
          <div class="hms-kpi k-amber" v-if="data.Stock">
            <i class="mdi mdi-pill hms-kpi-icon"></i>
            <div>
              <div class="hms-kpi-num">{{ formatNum(data.Stock.low_stock) }}</div>
              <div class="hms-kpi-lbl">Low Stock Items</div>
              <div class="hms-kpi-sub">{{ formatNum(data.Stock.out_of_stock) }} out · KSh {{ formatNum(data.Stock.stock_value) }} value</div>
            </div>
          </div>
        </div>

        <!-- Visit trend chart -->
        <div class="hms-card mb-3">
          <div class="hms-card-title"><i class="mdi mdi-chart-line me-1"></i>Visit Trend ({{ paddedTrend.length }} days)</div>
          <div class="hms-trend-wrap" v-if="paddedTrend.length">
            <svg viewBox="0 0 800 220" preserveAspectRatio="none" class="hms-trend-svg">
              <!-- Grid -->
              <line v-for="i in 4" :key="'g' + i" :x1="0" :y1="i * 44" :x2="800" :y2="i * 44" stroke="currentColor" stroke-opacity="0.1" />
              <!-- Lines (only if at least 2 points) -->
              <template v-if="paddedTrend.length >= 2">
                <polyline :points="trendPoints('emergency', 800, 200)"  fill="none" stroke="#dc3545" stroke-width="2" />
                <polyline :points="trendPoints('outpatient', 800, 200)" fill="none" stroke="#1a7fe8" stroke-width="2" />
                <polyline :points="trendPoints('total_visits', 800, 200)" fill="none" stroke="#34c38f" stroke-width="2.5" />
              </template>
              <!-- Dots at every data point (always rendered) -->
              <circle v-for="(p, i) in trendDots('total_visits', 800, 200)" :key="'tot' + i" :cx="p[0]" :cy="p[1]" :r="3.5" fill="#34c38f" />
              <circle v-for="(p, i) in trendDots('outpatient',   800, 200)" :key="'opd' + i" :cx="p[0]" :cy="p[1]" :r="2.5" fill="#1a7fe8" />
              <circle v-for="(p, i) in trendDots('emergency',    800, 200)" :key="'ed'  + i" :cx="p[0]" :cy="p[1]" :r="2.5" fill="#dc3545" />
            </svg>
            <div class="hms-trend-axis">
              <span v-for="(d, i) in paddedTrend" :key="'lbl' + i" v-show="i % Math.max(1, Math.ceil(paddedTrend.length / 8)) === 0">{{ shortDate(d.day) }}</span>
            </div>
            <div class="hms-trend-legend">
              <span><span class="dot" style="background:#34c38f"></span>Total ({{ trendSum('total_visits') }})</span>
              <span><span class="dot" style="background:#1a7fe8"></span>OPD ({{ trendSum('outpatient') }})</span>
              <span><span class="dot" style="background:#dc3545"></span>ED ({{ trendSum('emergency') }})</span>
            </div>
          </div>
          <div v-else class="hms-muted text-center py-3"><i class="mdi mdi-chart-line-variant me-1"></i>No visit data in this period.</div>
        </div>

        <div class="row g-3 mb-3">
          <!-- Bed occupancy -->
          <div class="col-lg-7">
            <div class="hms-card h-100">
              <div class="hms-card-title"><i class="mdi mdi-bed-empty me-1"></i>Bed Occupancy by Ward</div>
              <div v-if="data.Beds && data.Beds.length">
                <div v-for="w in data.Beds" :key="w.ward_id" class="hms-bed-row">
                  <div class="hms-bed-head">
                    <span class="hms-bed-name">{{ w.ward_name }} <span class="hms-muted-sm">{{ w.ward_type }}</span></span>
                    <span class="hms-muted-sm ms-auto">
                      {{ w.occupied }}/{{ w.total }}
                      <span class="hms-muted-sm">({{ pct(w.occupied, w.total) }}%)</span>
                    </span>
                  </div>
                  <div class="hms-bed-bar">
                    <div class="seg occ"   :style="{ width: pct(w.occupied, w.total) + '%' }"></div>
                    <div class="seg avail" :style="{ width: pct(w.available, w.total) + '%' }"></div>
                    <div class="seg unav"  :style="{ width: pct(w.unavailable, w.total) + '%' }"></div>
                  </div>
                </div>
                <div class="hms-bed-legend mt-2">
                  <span><span class="dot" style="background:#dc3545"></span>Occupied</span>
                  <span><span class="dot" style="background:#28a745"></span>Available</span>
                  <span><span class="dot" style="background:#94a3b8"></span>Unavailable</span>
                </div>
              </div>
              <div v-else class="hms-muted">No wards configured.</div>
            </div>
          </div>

          <!-- ED KPIs -->
          <div class="col-lg-5">
            <div class="hms-card h-100">
              <div class="hms-card-title"><i class="mdi mdi-hospital me-1"></i>ED Performance</div>
              <div v-if="data.EdKpi" class="hms-ed-kpis">
                <div class="hms-ed-row">
                  <div class="hms-ed-row-num">{{ Math.round(data.EdKpi.avg_door_to_triage) }}m</div>
                  <div class="hms-ed-row-lbl">Avg door-to-triage <span class="hms-muted-sm">(target ≤5m)</span></div>
                </div>
                <div class="hms-ed-row">
                  <div class="hms-ed-row-num" :class="data.EdKpi.avg_door_to_doctor > 60 ? 'text-warning' : ''">{{ Math.round(data.EdKpi.avg_door_to_doctor) }}m</div>
                  <div class="hms-ed-row-lbl">Avg door-to-doctor <span class="hms-muted-sm">(target ≤30m)</span></div>
                </div>
                <div class="hms-ed-row">
                  <div class="hms-ed-row-num">{{ Math.round(data.EdKpi.avg_los_minutes) }}m</div>
                  <div class="hms-ed-row-lbl">Avg ED length-of-stay</div>
                </div>
                <div class="hms-section-label mt-3">Dispositions ({{ period.replace('_',' ') }})</div>
                <div class="hms-disp-grid">
                  <div class="hms-disp"><div class="hms-disp-num">{{ data.EdKpi.d_admitted || 0 }}</div><div class="hms-disp-lbl">Admitted</div></div>
                  <div class="hms-disp"><div class="hms-disp-num text-success">{{ data.EdKpi.d_discharged || 0 }}</div><div class="hms-disp-lbl">Discharged</div></div>
                  <div class="hms-disp"><div class="hms-disp-num text-info">{{ data.EdKpi.d_theatre || 0 }}</div><div class="hms-disp-lbl">Theatre</div></div>
                  <div class="hms-disp"><div class="hms-disp-num text-amber">{{ data.EdKpi.d_referred || 0 }}</div><div class="hms-disp-lbl">Referred</div></div>
                  <div class="hms-disp"><div class="hms-disp-num text-muted">{{ data.EdKpi.d_lwbs || 0 }}</div><div class="hms-disp-lbl">LWBS</div></div>
                  <div class="hms-disp"><div class="hms-disp-num text-warning">{{ data.EdKpi.d_dama || 0 }}</div><div class="hms-disp-lbl">DAMA</div></div>
                  <div class="hms-disp"><div class="hms-disp-num text-danger">{{ data.EdKpi.d_morgue || 0 }}</div><div class="hms-disp-lbl">Mortuary</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-3 mb-3">
          <!-- Revenue by method -->
          <div class="col-lg-5">
            <div class="hms-card h-100">
              <div class="hms-card-title"><i class="mdi mdi-cash-register me-1"></i>Revenue by Payment Method</div>
              <div v-if="data.Revenue && data.Revenue.length">
                <div v-for="r in data.Revenue" :key="r.payment_method" class="hms-pay-row">
                  <div class="d-flex align-items-center justify-content-between mb-1">
                    <span class="fw-semibold">{{ formatPay(r.payment_method) }} <span class="hms-muted-sm">({{ r.cnt }})</span></span>
                    <span><strong>KSh {{ formatNum(r.total) }}</strong> <span class="hms-muted-sm">{{ pct(r.total, totalRevenue) }}%</span></span>
                  </div>
                  <div class="hms-bar">
                    <div class="hms-bar-fill" :class="payClass(r.payment_method)" :style="{ width: pct(r.total, totalRevenue) + '%' }"></div>
                  </div>
                </div>
              </div>
              <div v-else class="hms-muted">No payments in this period.</div>
            </div>
          </div>

          <!-- Demographics -->
          <div class="col-lg-7">
            <div class="hms-card h-100">
              <div class="hms-card-title"><i class="mdi mdi-account-group-outline me-1"></i>Patient Demographics</div>
              <div class="row g-3">
                <div class="col-md-6">
                  <div class="hms-section-label">SEX</div>
                  <div v-for="g in data.DemoGender" :key="g.gender || 'unknown'" class="hms-pay-row">
                    <div class="d-flex align-items-center justify-content-between mb-1">
                      <span class="fw-semibold">{{ (g.gender || 'unknown').charAt(0).toUpperCase() + (g.gender || 'unknown').slice(1) }}</span>
                      <span><strong>{{ formatNum(g.cnt) }}</strong> <span class="hms-muted-sm">{{ pct(g.cnt, totalGender) }}%</span></span>
                    </div>
                    <div class="hms-bar"><div class="hms-bar-fill" :class="genderClass(g.gender)" :style="{ width: pct(g.cnt, totalGender) + '%' }"></div></div>
                  </div>
                </div>
                <div class="col-md-6" v-if="data.DemoAge">
                  <div class="hms-section-label">AGE GROUPS</div>
                  <div v-for="b in ageBuckets" :key="b.key" class="hms-pay-row">
                    <div class="d-flex align-items-center justify-content-between mb-1">
                      <span>{{ b.label }}</span>
                      <span><strong>{{ formatNum(data.DemoAge[b.key]) }}</strong> <span class="hms-muted-sm">{{ pct(data.DemoAge[b.key], ageTotal) }}%</span></span>
                    </div>
                    <div class="hms-bar"><div class="hms-bar-fill bar-info" :style="{ width: pct(data.DemoAge[b.key], ageTotal) + '%' }"></div></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-3 mb-3">
          <!-- Top diagnoses -->
          <div class="col-lg-6">
            <div class="hms-card h-100">
              <div class="hms-card-title"><i class="mdi mdi-clipboard-pulse me-1"></i>Top 10 Diagnoses</div>
              <div v-if="data.TopDx && data.TopDx.length" class="hms-rank-list">
                <div v-for="(d, i) in data.TopDx" :key="i" class="hms-rank-row">
                  <span class="hms-rank">{{ i + 1 }}</span>
                  <div class="flex-fill">
                    <div>{{ d.diagnosis_name }} <span class="hms-mono hms-muted-sm">{{ d.icd_code }}</span></div>
                    <div class="hms-bar"><div class="hms-bar-fill bar-purple" :style="{ width: pct(d.cnt, data.TopDx[0].cnt) + '%' }"></div></div>
                  </div>
                  <span class="hms-rank-val">{{ d.cnt }}</span>
                </div>
              </div>
              <div v-else class="hms-muted">No diagnoses in this period.</div>
            </div>
          </div>

          <!-- Top drugs -->
          <div class="col-lg-6">
            <div class="hms-card h-100">
              <div class="hms-card-title"><i class="mdi mdi-pill me-1"></i>Top 10 Medications</div>
              <div v-if="data.TopDrugs && data.TopDrugs.length" class="hms-rank-list">
                <div v-for="(d, i) in data.TopDrugs" :key="i" class="hms-rank-row">
                  <span class="hms-rank">{{ i + 1 }}</span>
                  <div class="flex-fill">
                    <div>{{ d.drug_name }}</div>
                    <div class="hms-bar"><div class="hms-bar-fill bar-green" :style="{ width: pct(d.rx_count, data.TopDrugs[0].rx_count) + '%' }"></div></div>
                  </div>
                  <span class="hms-rank-val">{{ d.rx_count }} <span class="hms-muted-sm">prescriptions</span></span>
                </div>
              </div>
              <div v-else class="hms-muted">No prescriptions in this period.</div>
            </div>
          </div>
        </div>

        <div class="row g-3 mb-3">
          <!-- Lab + Radiology -->
          <div class="col-lg-6">
            <div class="hms-card h-100">
              <div class="hms-card-title"><i class="mdi mdi-flask me-1"></i>Diagnostics Throughput</div>
              <div class="row g-3" v-if="data.LabRad">
                <div class="col-6">
                  <div class="hms-mini-card">
                    <div class="hms-mini-num">{{ formatNum(data.LabRad.lab_requests) }}</div>
                    <div class="hms-mini-lbl">Lab requests</div>
                    <div class="hms-muted-sm">{{ formatNum(data.LabRad.lab_resulted) }} resulted</div>
                    <div class="hms-mini-foot">TAT {{ Math.round(data.LabRad.avg_lab_tat_hours) }}h</div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="hms-mini-card">
                    <div class="hms-mini-num">{{ formatNum(data.LabRad.rad_requests) }}</div>
                    <div class="hms-mini-lbl">Imaging studies</div>
                    <div class="hms-muted-sm">{{ formatNum(data.LabRad.rad_reported) }} reported</div>
                    <div class="hms-mini-foot">TAT {{ Math.round(data.LabRad.avg_rad_tat_hours) }}h</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Disease registries -->
          <div class="col-lg-6">
            <div class="hms-card h-100">
              <div class="hms-card-title"><i class="mdi mdi-database me-1"></i>Disease Registries</div>
              <div class="hms-reg-grid" v-if="data.Registries">
                <div class="hms-reg" v-for="r in registriesList" :key="r.key">
                  <i class="mdi" :class="r.icon"></i>
                  <div>
                    <div class="hms-reg-num">{{ formatNum(data.Registries[r.key]) }}</div>
                    <div class="hms-reg-lbl">{{ r.label }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-3 mb-3">
          <!-- Referrals -->
          <div class="col-lg-6">
            <div class="hms-card h-100">
              <div class="hms-card-title"><i class="mdi mdi-truck-fast-outline me-1"></i>Referrals</div>
              <div class="hms-reg-grid" v-if="data.Referrals">
                <div class="hms-reg">
                  <i class="mdi mdi-tray-arrow-down" style="color: var(--hms-info)"></i>
                  <div><div class="hms-reg-num">{{ formatNum(data.Referrals.inbound) }}</div><div class="hms-reg-lbl">Inbound</div></div>
                </div>
                <div class="hms-reg">
                  <i class="mdi mdi-tray-arrow-up" style="color: var(--hms-success)"></i>
                  <div><div class="hms-reg-num">{{ formatNum(data.Referrals.outbound) }}</div><div class="hms-reg-lbl">Outbound</div></div>
                </div>
                <div class="hms-reg">
                  <i class="mdi mdi-tray-alert" style="color: var(--hms-warning)"></i>
                  <div><div class="hms-reg-num">{{ formatNum(data.Referrals.inbound_pending) }}</div><div class="hms-reg-lbl">Awaiting decision</div></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Staff -->
          <div class="col-lg-6">
            <div class="hms-card h-100">
              <div class="hms-card-title"><i class="mdi mdi-account-tie me-1"></i>Staff & HR</div>
              <div class="hms-reg-grid" v-if="data.Staff">
                <div class="hms-reg">
                  <i class="mdi mdi-account-multiple" style="color: var(--hms-info)"></i>
                  <div><div class="hms-reg-num">{{ formatNum(data.Staff.active_staff) }}</div><div class="hms-reg-lbl">Active staff</div></div>
                </div>
                <div class="hms-reg">
                  <i class="mdi mdi-license" style="color: var(--hms-warning)"></i>
                  <div><div class="hms-reg-num">{{ formatNum(data.Staff.licenses_expiring) }}</div><div class="hms-reg-lbl">Licences expiring (90d)</div></div>
                </div>
                <div class="hms-reg" v-if="data.Staff.licenses_expired > 0">
                  <i class="mdi mdi-alert-octagon" style="color: var(--hms-danger)"></i>
                  <div><div class="hms-reg-num text-danger">{{ formatNum(data.Staff.licenses_expired) }}</div><div class="hms-reg-lbl">Expired licences</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="hms-muted-sm text-center mt-4">
          Generated {{ new Date().toLocaleString() }} · Period: {{ period.replace('_',' ') }}{{ branchFilter ? ' · Branch: ' + (branches.find(b => b.branch_id == branchFilter) || {}).branch_name : ' · All branches' }}
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }

@media print {
  .hms-page-header, .hms-portal-nav, .hms-stat:hover, button { display: none !important; }
  .hms-page { padding-top: 0 !important; }
  .hms-card { break-inside: avoid; }
}
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 24px 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted    { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono     { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.text-success { color: var(--hms-success); }
.text-info    { color: var(--hms-info); }
.text-warning { color: var(--hms-warning); }
.text-danger  { color: var(--hms-danger); }
.text-amber   { color: #f59e0b; }
.text-muted   { color: var(--hms-m); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-card-title { color: var(--hms-h); font-weight: 700; font-size: 14px; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 1px; }
.hms-spinner { display: flex; justify-content: center; padding: 60px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }

.hms-input { background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 7px 12px; color: var(--hms-h); font-size: 13px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; }

.hms-btn { border: none; border-radius: 8px; padding: 7px 14px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }

/* KPI cards */
.hms-kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-bottom: 18px; }
.hms-kpi { display: flex; gap: 12px; align-items: center; background: var(--hms-bg1); border: 1px solid var(--hms-br); border-left: 4px solid var(--hms-primary); border-radius: 12px; padding: 14px 16px; }
.hms-kpi-icon { font-size: 32px; flex-shrink: 0; }
.hms-kpi-num { font-size: 22px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-kpi-lbl { color: var(--hms-s); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; font-weight: 600; }
.hms-kpi-sub { color: var(--hms-m); font-size: 11px; margin-top: 2px; }
.hms-kpi.k-blue   { border-left-color: var(--hms-primary); }     .hms-kpi.k-blue   .hms-kpi-icon { color: var(--hms-primary); }
.hms-kpi.k-purple { border-left-color: #9b59b6; }                 .hms-kpi.k-purple .hms-kpi-icon { color: #9b59b6; }
.hms-kpi.k-green  { border-left-color: var(--hms-success); }     .hms-kpi.k-green  .hms-kpi-icon { color: var(--hms-success); }
.hms-kpi.k-red    { border-left-color: var(--hms-danger); }      .hms-kpi.k-red    .hms-kpi-icon { color: var(--hms-danger); }
.hms-kpi.k-amber  { border-left-color: #f59e0b; }                 .hms-kpi.k-amber  .hms-kpi-icon { color: #f59e0b; }
.hms-kpi.k-info   { border-left-color: var(--hms-info); }         .hms-kpi.k-info   .hms-kpi-icon { color: var(--hms-info); }
.hms-kpi.k-neutral{ border-left-color: var(--hms-br); }           .hms-kpi.k-neutral .hms-kpi-icon{ color: var(--hms-m); }

/* Trend chart */
.hms-trend-wrap { color: var(--hms-h); }
.hms-trend-svg { width: 100%; height: 220px; }
.hms-trend-axis { display: flex; justify-content: space-between; padding: 4px 0; color: var(--hms-s); font-size: 11px; }
.hms-trend-legend { display: flex; gap: 16px; margin-top: 6px; font-size: 12px; color: var(--hms-h); }
.hms-trend-legend .dot,
.hms-bed-legend .dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 4px; vertical-align: middle; }

/* Bed bars */
.hms-bed-row { margin-bottom: 10px; }
.hms-bed-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.hms-bed-name { color: var(--hms-h); font-weight: 600; font-size: 13px; }
.hms-bed-bar { display: flex; height: 10px; background: var(--hms-bg3); border-radius: 50px; overflow: hidden; }
.hms-bed-bar .seg.occ   { background: var(--hms-danger); }
.hms-bed-bar .seg.avail { background: var(--hms-success); }
.hms-bed-bar .seg.unav  { background: #94a3b8; }
.hms-bed-legend { display: flex; gap: 14px; font-size: 12px; color: var(--hms-s); }

/* ED KPIs */
.hms-ed-kpis .hms-ed-row { padding: 6px 0; border-bottom: 1px dashed var(--hms-br); }
.hms-ed-kpis .hms-ed-row:last-of-type { border-bottom: none; }
.hms-ed-row-num { font-size: 22px; font-weight: 800; color: var(--hms-h); }
.hms-ed-row-lbl { font-size: 12px; color: var(--hms-s); }
.hms-disp-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
.hms-disp { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 8px; text-align: center; }
.hms-disp-num { font-size: 16px; font-weight: 800; color: var(--hms-h); }
.hms-disp-lbl { font-size: 10px; color: var(--hms-s); text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }

/* Section labels */
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; }

/* Bars */
.hms-pay-row { margin-bottom: 10px; font-size: 13px; color: var(--hms-h); }
.hms-bar { height: 8px; background: var(--hms-bg3); border-radius: 50px; overflow: hidden; }
.hms-bar-fill { height: 100%; background: var(--hms-primary); transition: width .3s; }
.hms-bar-fill.pay-cash      { background: var(--hms-success); }
.hms-bar-fill.pay-mpesa     { background: #16a085; }
.hms-bar-fill.pay-card      { background: var(--hms-info); }
.hms-bar-fill.pay-insurance { background: #9b59b6; }
.hms-bar-fill.pay-corporate { background: #e84393; }
.hms-bar-fill.pay-bank      { background: #34495e; }
.hms-bar-fill.pay-waiver    { background: var(--hms-warning); }
.hms-bar-fill.bar-green     { background: var(--hms-success); }
.hms-bar-fill.bar-purple    { background: #9b59b6; }
.hms-bar-fill.bar-info      { background: var(--hms-info); }
.hms-bar-fill.gen-male      { background: #1a7fe8; }
.hms-bar-fill.gen-female    { background: #e84393; }
.hms-bar-fill.gen-other     { background: var(--hms-m); }

/* Rank lists */
.hms-rank-list { display: flex; flex-direction: column; gap: 8px; }
.hms-rank-row { display: flex; gap: 10px; align-items: center; }
.hms-rank { width: 24px; height: 24px; border-radius: 50%; background: var(--hms-bg3); color: var(--hms-h); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; flex-shrink: 0; }
.hms-rank-val { color: var(--hms-h); font-weight: 700; font-size: 13px; flex-shrink: 0; }

/* Mini cards */
.hms-mini-card { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 14px; text-align: center; }
.hms-mini-num { font-size: 26px; font-weight: 800; color: var(--hms-h); }
.hms-mini-lbl { font-size: 11px; color: var(--hms-s); text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }
.hms-mini-foot { color: var(--hms-primary); font-weight: 700; font-size: 12px; margin-top: 6px; padding-top: 6px; border-top: 1px dashed var(--hms-br); }

/* Registries grid */
.hms-reg-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; }
.hms-reg { display: flex; gap: 10px; align-items: center; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 14px; }
.hms-reg i { font-size: 26px; color: var(--hms-primary); }
.hms-reg-num { color: var(--hms-h); font-weight: 800; font-size: 18px; }
.hms-reg-lbl { color: var(--hms-s); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }

/* Date range picker + preset chips */
.hms-daterange { display: inline-flex; align-items: center; gap: 6px; padding: 4px 8px; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; }
.hms-daterange-lbl { font-size: 11px; color: var(--hms-s); text-transform: uppercase; letter-spacing: .5px; margin: 0; }
.hms-daterange .hms-input { padding: 4px 8px; font-size: 12px; min-width: 130px; }
.hms-preset-chips { display: inline-flex; gap: 4px; flex-wrap: wrap; }
.hms-chip { padding: 4px 10px; font-size: 11px; font-weight: 600; border-radius: 12px; background: var(--hms-bg2); color: var(--hms-s); border: 1px solid var(--hms-br); cursor: pointer; text-transform: uppercase; letter-spacing: .3px; }
.hms-chip:hover { background: var(--hms-bg3); color: var(--hms-h); }
.hms-chip.active { background: var(--hms-primary); color: #fff; border-color: var(--hms-primary); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "ReportsDashboard",
  components: { Navigation },
  data() {
    return {
      loading: true,
      startDate: '',
      endDate: '',
      activePreset: 'this_month',
      presets: [
        { key: 'today',      label: 'Today' },
        { key: 'this_week',  label: 'This Week' },
        { key: 'this_month', label: 'This Month' },
        { key: 'last_7',     label: 'Last 7 days' },
        { key: 'last_30',    label: 'Last 30 days' },
        { key: 'this_year',  label: 'This Year' }
      ],
      branchFilter: '',
      branches: [],
      data: {},
      ageBuckets: [
        { key:'under_5',  label:'Under 5' },
        { key:'age_5_17', label:'5–17' },
        { key:'age_18_34',label:'18–34' },
        { key:'age_35_59',label:'35–59' },
        { key:'over_60',  label:'60+' }
      ],
      registriesList: [
        { key:'hiv',           label:'HIV',           icon:'mdi-virus' },
        { key:'tb',            label:'TB',            icon:'mdi-lungs' },
        { key:'cancer',        label:'Cancer',        icon:'mdi-ribbon' },
        { key:'diabetes',      label:'Diabetes',      icon:'mdi-water-percent' },
        { key:'mch',           label:'MCH',           icon:'mdi-baby-carriage' },
        { key:'mental_health', label:'Mental Health', icon:'mdi-head-heart' },
        { key:'dialysis',      label:'Dialysis',      icon:'mdi-water-pump' },
        { key:'nutrition',     label:'Nutrition',     icon:'mdi-food-apple' }
      ]
    };
  },
  computed: {
    today() { return new Date().toISOString().slice(0, 10); },
    totalRevenue() {
      return (this.data.Revenue || []).reduce((s, r) => s + (parseFloat(r.total) || 0), 0);
    },
    totalGender() {
      return (this.data.DemoGender || []).reduce((s, g) => s + (parseInt(g.cnt) || 0), 0);
    },
    ageTotal() {
      var a = this.data.DemoAge || {};
      return ['under_5','age_5_17','age_18_34','age_35_59','over_60'].reduce((s, k) => s + (parseInt(a[k]) || 0), 0);
    },
    trendMax() {
      if (!this.paddedTrend.length) return 1;
      return Math.max(1, ...this.paddedTrend.map(d => parseInt(d.total_visits) || 0));
    },
    paddedTrend() {
      // Build a continuous date axis so the chart shows zeros on quiet days
      var raw = this.data.Trend || [];
      var byDay = {};
      raw.forEach(d => {
        var k = (d.day || '').slice(0, 10);
        byDay[k] = d;
      });
      // Window length per period
      var days = ({ today:1, this_week:7, last_7:7, this_month:30, last_30:30, this_year:30 })[this.period] || 7;
      // Anchor end at the latest day in the data, falling back to today
      var keys = Object.keys(byDay).sort();
      var endStr = keys.length ? keys[keys.length - 1] : new Date().toISOString().slice(0, 10);
      var end = new Date(endStr + 'T00:00:00');
      var result = [];
      for (var i = days - 1; i >= 0; i--) {
        var d = new Date(end);
        d.setDate(d.getDate() - i);
        var key = d.toISOString().slice(0, 10);
        result.push(byDay[key] || { day: key, total_visits: 0, emergency: 0, outpatient: 0 });
      }
      return result;
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    // Seed the calendar to the "This Month" preset window
    this.applyPreset('this_month');
    this.loadBranches();
  },
  methods: {
    async loadBranches() {
      try {
        var r = await executeJson({ function: "getBranches" }, hms);
        if (r.data && r.data.success) this.branches = (r.data.data && r.data.data.Branches) || [];
      } catch(e) {}
    },
    async loadAll() {
      this.loading = true;
      try {
        // Send explicit date range if picked; backend also accepts preset as fallback.
        var payload = { function: "getReports" };
        if (this.startDate && this.endDate) {
          payload.start_date = this.startDate;
          payload.end_date   = this.endDate;
        } else {
          payload.period = this.activePreset;
        }
        if (this.branchFilter) payload.branch_id = this.branchFilter;
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) this.data = r.data.data || {};
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    applyPreset(key) {
      this.activePreset = key;
      var today = new Date();
      var end   = new Date(today);
      var start = new Date(today);
      switch (key) {
        case 'today':      break;
        case 'this_week':  start.setDate(today.getDate() - ((today.getDay() + 6) % 7)); break;
        case 'this_month': start = new Date(today.getFullYear(), today.getMonth(), 1); break;
        case 'this_year':  start = new Date(today.getFullYear(), 0, 1); break;
        case 'last_7':     start.setDate(today.getDate() - 6); break;
        case 'last_30':    start.setDate(today.getDate() - 29); break;
      }
      this.startDate = start.toISOString().slice(0, 10);
      this.endDate   = end.toISOString().slice(0, 10);
      this.loadAll();
    },
    onDateChange() {
      // User-driven date pick clears the "active preset" highlight.
      this.activePreset = null;
      if (this.startDate && this.endDate) this.loadAll();
    },
    printReport() { window.print(); },

    formatNum(n) { if (n == null || n === '') return '0'; return Number(n).toLocaleString(); },
    pct(part, whole) {
      var p = parseFloat(part) || 0;
      var w = parseFloat(whole) || 0;
      if (w <= 0) return 0;
      return Math.round((p / w) * 100);
    },
    shortDate(s) { if (!s) return ''; var d = new Date(s); if (isNaN(d)) return s; return (d.getMonth() + 1) + '/' + d.getDate(); },

    trendPoints(key, w, h) {
      var data = this.paddedTrend.map(d => parseInt(d[key]) || 0);
      if (data.length < 2) return '';
      var max = this.trendMax || 1;
      var step = w / Math.max(1, data.length - 1);
      return data.map((d, i) => {
        var x = i * step;
        var y = h - (d / max) * (h - 10) - 5;
        return x.toFixed(1) + ',' + y.toFixed(1);
      }).join(' ');
    },
    trendDots(key, w, h) {
      var data = this.paddedTrend.map(d => parseInt(d[key]) || 0);
      if (!data.length) return [];
      var max = this.trendMax || 1;
      var step = data.length === 1 ? w / 2 : w / (data.length - 1);
      return data.map((d, i) => {
        var x = data.length === 1 ? w / 2 : i * step;
        var y = h - (d / max) * (h - 10) - 5;
        return [x.toFixed(1), y.toFixed(1)];
      });
    },
    trendSum(key) {
      return this.paddedTrend.reduce((s, d) => s + (parseInt(d[key]) || 0), 0);
    },

    formatPay(p) { return ({ cash:'Cash', mpesa:'M-Pesa', card:'Card', bank_transfer:'Bank', insurance:'Insurance', corporate:'Corporate', waiver:'Waiver' })[p] || p; },
    payClass(p) { return ({ cash:'pay-cash', mpesa:'pay-mpesa', card:'pay-card', insurance:'pay-insurance', corporate:'pay-corporate', bank_transfer:'pay-bank', waiver:'pay-waiver' })[p] || ''; },
    genderClass(g) { return ({ male:'gen-male', female:'gen-female' })[g] || 'gen-other'; }
  }
};
</script>
